/**
 * Indicator Presets
 *
 * Pre-configured settings for common trading strategies
 */

export const INDICATOR_PRESETS = {
  macd: {
    standard: {
      name: 'Standard',
      description: 'Traditional MACD (12, 26, 9) with EMA',
      settings: {
        fast: 12,
        slow: 26,
        signal: 9,
        ma_type: 'ema',
        source: 'close'
      }
    },
    fast: {
      name: 'Fast',
      description: 'Responsive MACD (8, 21, 5) for short-term trading',
      settings: {
        fast: 8,
        slow: 21,
        signal: 5,
        ma_type: 'ema',
        source: 'close'
      }
    },
    slow: {
      name: 'Slow',
      description: 'Smooth MACD (19, 39, 9) for longer timeframes',
      settings: {
        fast: 19,
        slow: 39,
        signal: 9,
        ma_type: 'ema',
        source: 'close'
      }
    }
  },

  rsi: {
    standard: {
      name: 'Standard',
      description: 'Traditional RSI (14) with 70/30 levels',
      settings: {
        period: 14,
        overbought: 70,
        oversold: 30,
        source: 'close'
      }
    },
    aggressive: {
      name: 'Aggressive',
      description: 'Sensitive RSI (9) with 80/20 levels',
      settings: {
        period: 9,
        overbought: 80,
        oversold: 20,
        source: 'close'
      }
    },
    conservative: {
      name: 'Conservative',
      description: 'Smooth RSI (21) with 65/35 levels',
      settings: {
        period: 21,
        overbought: 65,
        oversold: 35,
        source: 'close'
      }
    }
  },

  bollinger: {
    standard: {
      name: 'Standard',
      description: 'Traditional Bollinger Bands (20, 2.0)',
      settings: {
        period: 20,
        deviations_upper: 2.0,
        deviations_lower: 2.0,
        ma_type: 'sma',
        source: 'close',
        displace: 0
      }
    },
    tight: {
      name: 'Tight',
      description: 'Narrow bands (20, 1.5) for low volatility',
      settings: {
        period: 20,
        deviations_upper: 1.5,
        deviations_lower: 1.5,
        ma_type: 'sma',
        source: 'close',
        displace: 0
      }
    },
    wide: {
      name: 'Wide',
      description: 'Wide bands (20, 2.5) for high volatility',
      settings: {
        period: 20,
        deviations_upper: 2.5,
        deviations_lower: 2.5,
        ma_type: 'sma',
        source: 'close',
        displace: 0
      }
    },
    asymmetric: {
      name: 'Asymmetric',
      description: 'Different upper/lower bands (20, 2.5/1.5)',
      settings: {
        period: 20,
        deviations_upper: 2.5,
        deviations_lower: 1.5,
        ma_type: 'sma',
        source: 'close',
        displace: 0
      }
    }
  },

  volume: {
    standard: {
      name: 'Standard',
      description: '20-period SMA of volume',
      settings: {
        ma_period: 20,
        ma_type: 'sma'
      }
    },
    fast: {
      name: 'Fast',
      description: '10-period EMA of volume',
      settings: {
        ma_period: 10,
        ma_type: 'ema'
      }
    },
    slow: {
      name: 'Slow',
      description: '50-period SMA of volume',
      settings: {
        ma_period: 50,
        ma_type: 'sma'
      }
    }
  },

  sma: {
    short: {
      name: 'Short-term',
      description: '20-period for short-term trends',
      settings: {
        period: 20,
        displace: 0,
        source: 'close'
      }
    },
    medium: {
      name: 'Medium-term',
      description: '50-period for medium-term trends',
      settings: {
        period: 50,
        displace: 0,
        source: 'close'
      }
    },
    long: {
      name: 'Long-term',
      description: '200-period for long-term trends',
      settings: {
        period: 200,
        displace: 0,
        source: 'close'
      }
    },
    veryLong: {
      name: 'Very Long-term',
      description: '720-period (~3 years)',
      settings: {
        period: 720,
        displace: 0,
        source: 'close'
      }
    }
  },

  ema: {
    short: {
      name: 'Short-term',
      description: '12-period for quick response',
      settings: {
        period: 12,
        displace: 0,
        source: 'close'
      }
    },
    medium: {
      name: 'Medium-term',
      description: '26-period for balanced response',
      settings: {
        period: 26,
        displace: 0,
        source: 'close'
      }
    },
    long: {
      name: 'Long-term',
      description: '50-period for smooth trends',
      settings: {
        period: 50,
        displace: 0,
        source: 'close'
      }
    }
  }
};

/**
 * Get presets for a specific indicator
 * @param {string} indicatorName - Name of the indicator
 * @returns {object} Preset configurations
 */
export const getPresetsForIndicator = (indicatorName) => {
  // Handle SMA variants
  if (indicatorName.startsWith('sma_')) {
    return INDICATOR_PRESETS.sma;
  }

  return INDICATOR_PRESETS[indicatorName] || {};
};

/**
 * Apply a preset to current settings
 * @param {object} currentSettings - Current indicator settings
 * @param {object} presetSettings - Preset to apply
 * @returns {object} Merged settings
 */
export const applyPreset = (currentSettings, presetSettings) => {
  return {
    ...currentSettings,
    ...presetSettings,
    // Keep colors from current settings if they exist
    colors: currentSettings.colors || {}
  };
};
